import React, { useState, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import { Save, Download, Upload, Maximize2, Plus, X, Edit2, Check, ArrowLeft, Clock, Grid } from 'lucide-react';
import { useERDStore } from './erdStore';

export default function ERDTopBar({ onShowRevisions }) {
  const {
    fileName, pages, currentPageId, isDirty, saveStatus, snapToGrid,
    setCurrentPage, addPage, deletePage, renamePage,
    saveDocument, exportToFile, closeDocument, setFileName, toggleSnapToGrid,
  } = useERDStore();
  const { fitView } = useReactFlow();

  const [editingPageId, setEditingPageId] = useState(null);
  const [editingPageName, setEditingPageName] = useState('');
  const [editingFileName, setEditingFileName] = useState(false);
  const [fileNameDraft, setFileNameDraft] = useState(fileName);
  const pageNameRef = useRef(null);

  function startRenameFile() {
    setFileNameDraft(fileName);
    setEditingFileName(true);
  }
  function commitRenameFile() {
    if (fileNameDraft.trim()) setFileName(fileNameDraft.trim());
    setEditingFileName(false);
  }

  function startRenamePage(id, name) {
    setEditingPageId(id);
    setEditingPageName(name);
    setTimeout(() => pageNameRef.current?.select(), 10);
  }
  function commitRenamePage() {
    if (editingPageId && editingPageName.trim()) renamePage(editingPageId, editingPageName.trim());
    setEditingPageId(null);
  }

  async function handleSave() {
    try { await saveDocument(); } catch (e) { /* handled in store */ }
  }

  function handleImport() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.ferd.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        if (data.pages) {
          const { importFromFile } = useERDStore.getState();
          await importFromFile(data);
        }
      } catch (err) {
        console.error('Failed to import file:', err);
      }
    };
    input.click();
  }

  const statusLabel = saveStatus === 'saving' ? 'Saving...' : saveStatus === 'error' ? 'Save failed' : isDirty ? 'Unsaved' : 'Saved';
  const statusClass = saveStatus === 'saving' ? 'saving' : saveStatus === 'error' ? 'error' : isDirty ? 'unsaved' : 'saved';

  return (
    <div className="erd-topbar">
      {/* File row */}
      <div className="erd-topbar-file-row">
        <button onClick={closeDocument} className="erd-topbar-btn" title="Back to documents">
          <ArrowLeft size={15} />
          {<span>Back</span>}
        </button>

        <div className="erd-topbar-divider" />

        <div className="erd-topbar-filename">
          {editingFileName ? (
            <input
              autoFocus
              value={fileNameDraft}
              onChange={e => setFileNameDraft(e.target.value)}
              onBlur={commitRenameFile}
              onKeyDown={e => { if (e.key === 'Enter') commitRenameFile(); if (e.key === 'Escape') setEditingFileName(false); }}
              className="erd-topbar-filename-input"
            />
          ) : (
            <button onClick={startRenameFile} className="erd-topbar-filename-btn" title="Click to rename">
              {fileName}
              {isDirty && <span className="erd-dirty-dot">•</span>}
              <Edit2 size={11} className="erd-edit-icon" />
            </button>
          )}
        </div>

        <span className={`erd-save-status ${statusClass}`}>{statusLabel}</span>

        <div className="erd-topbar-spacer" />

        <button onClick={handleSave} className="erd-topbar-btn" title="Save (Ctrl+S)">
          <Save size={15} /><span>Save</span>
        </button>
        <button onClick={exportToFile} className="erd-topbar-btn" title="Export as .ferd.json">
          <Download size={15} /><span>Export</span>
        </button>
        <button onClick={handleImport} className="erd-topbar-btn" title="Import .ferd.json">
          <Upload size={15} /><span>Import</span>
        </button>
        <button onClick={onShowRevisions} className="erd-topbar-btn" title="Revision history">
          <Clock size={15} /><span>History</span>
        </button>

        <div className="erd-topbar-divider" />
        <button
          onClick={toggleSnapToGrid}
          className={`erd-topbar-btn ${snapToGrid ? 'erd-topbar-btn-active' : ''}`}
          title={snapToGrid ? 'Disable snap to grid' : 'Enable snap to grid'}
        >
          <Grid size={15} />
        </button>
        <button onClick={() => fitView({ padding: 0.15, duration: 400 })} className="erd-topbar-btn" title="Fit view">
          <Maximize2 size={15} />
        </button>
      </div>

      {/* Pages row */}
      <div className="erd-topbar-pages-row">
        {pages.map(page => (
          <div
            key={page.id}
            className={`erd-page-tab ${page.id === currentPageId ? 'active' : ''}`}
            onClick={() => setCurrentPage(page.id)}
            onDoubleClick={() => startRenamePage(page.id, page.name)}
          >
            {editingPageId === page.id ? (
              <>
                <input
                  ref={pageNameRef}
                  autoFocus
                  value={editingPageName}
                  onChange={e => setEditingPageName(e.target.value)}
                  onBlur={commitRenamePage}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitRenamePage();
                    if (e.key === 'Escape') setEditingPageId(null);
                    e.stopPropagation();
                  }}
                  onClick={e => e.stopPropagation()}
                  className="erd-page-tab-input"
                />
                <button onClick={e => { e.stopPropagation(); commitRenamePage(); }} className="erd-page-tab-confirm">
                  <Check size={12} />
                </button>
              </>
            ) : (
              <>
                <span className="erd-page-tab-name">{page.name}</span>
                {pages.length > 1 && (
                  <button
                    onClick={e => { e.stopPropagation(); deletePage(page.id); }}
                    className="erd-page-tab-delete"
                    title="Delete page"
                  >
                    <X size={11} />
                  </button>
                )}
              </>
            )}
          </div>
        ))}
        <button onClick={() => addPage()} className="erd-page-tab-add" title="Add page">
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}
